if (!customElements.get('bundle-variant-cards')) {
  customElements.define(
    'bundle-variant-cards',
    class BundleVariantCards extends HTMLElement {
      constructor() {
        super();
        this.onChangeBound = this.onChange.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.addEventListener('change', this.onChangeBound);

        // Progress bar steps (optional): clicking a step selects its offer.
        this.querySelectorAll('[data-bvc-step-for]').forEach((step) => {
          step.addEventListener('click', () => {
            const radio = Array.from(this.querySelectorAll('input[data-bvc-radio]')).find(
              (input) => input.value === step.dataset.bvcStepFor
            );
            if (radio && !radio.disabled && !radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });

        // Keep cards in sync when the variant is changed elsewhere
        // (sticky ATC picker, another variant picker block, etc.)
        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe('variant-change', (event) => {
            if (!event || !event.data || String(event.data.sectionId) !== String(this.sectionId)) return;
            const variant = event.data.variant;
            if (!variant) return;
            // Ignore stale publishes (slow fetches resolving after a newer
            // selection): the section's form input is the source of truth.
            const formInput = document.querySelector('#product-form-' + this.sectionId + ' input[name="id"]');
            if (formInput && String(variant.id) !== String(formInput.value)) return;
            const radio = Array.from(this.querySelectorAll('input[type="radio"][data-bvc-radio]')).find(
              (input) => input.value === variant.option1
            );
            // Only touch the DOM when the state actually changed, so repeated
            // publishes can never restart running animations.
            if (radio && !radio.checked) {
              radio.checked = true;
              this.syncState();
            }
          });
        }

        // Purchase mode (one-time / subscription): follows the subscription
        // widget of this section. Gifts can differ per mode, so gift
        // activation depends on it. Without a widget, mode stays one-time.
        this.subMode = false;
        const widget = document.querySelector(
          'bundle-subscription-widget[data-section="' + this.sectionId + '"]'
        );
        if (widget && widget.querySelector('input[data-bsw-radio][value="sub"]:checked')) {
          this.subMode = true;
        }
        const wrapper = this.closest('.bundle-variant-cards-wrapper');
        if (wrapper) wrapper.classList.toggle('bvc-mode-sub', this.subMode);
        this.onPlanModeChange = (event) => {
          if (!event.detail || String(event.detail.sectionId) !== String(this.sectionId)) return;
          const sub = !!event.detail.sub;
          if (sub === this.subMode) return;
          this.subMode = sub;
          const wrap = this.closest('.bundle-variant-cards-wrapper');
          if (wrap) wrap.classList.toggle('bvc-mode-sub', sub);
          this.syncState();
        };
        document.addEventListener('bundle:selling-plan-change', this.onPlanModeChange);

        this.initDefaultSelection();
        this.syncState();

        this.fitChips();
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.fitChips());
        }
        this.onResizeBound = () => {
          clearTimeout(this.chipResizeTimer);
          this.chipResizeTimer = setTimeout(() => this.fitChips(), 150);
        };
        window.addEventListener('resize', this.onResizeBound);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChangeBound);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.onPlanModeChange) document.removeEventListener('bundle:selling-plan-change', this.onPlanModeChange);
        if (this.onResizeBound) window.removeEventListener('resize', this.onResizeBound);
        clearTimeout(this.chipResizeTimer);
      }

      // Progress bar chips must stay on ONE line and inside the panel. A chip
      // is centered on its step's circle, so the room it has is twice the
      // distance from that center to the nearest panel edge: shrink the font
      // (padding is em-based, so it shrinks along) until the chip fits.
      fitChips() {
        const panel = this.querySelector('.bvc-progress');
        if (!panel) return;
        const panelRect = panel.getBoundingClientRect();
        if (!panelRect.width) return;
        this.querySelectorAll('.bpb-step__chip').forEach((chip) => {
          chip.style.fontSize = '';
          chip.style.transform = '';
          const rect = chip.getBoundingClientRect();
          if (!rect.width) return;
          const center = rect.left + rect.width / 2;
          const budget = 2 * Math.min(center - panelRect.left, panelRect.right - center) - 2;
          if (budget > 0 && rect.width > budget) {
            const base = parseFloat(window.getComputedStyle(chip).fontSize);
            chip.style.fontSize = Math.max(9, (base * budget) / rect.width) + 'px';
          }
          // At the 9px font floor a chip can still poke past the panel edge:
          // nudge it inward as a last resort (breaks centering, never the panel).
          const fitted = chip.getBoundingClientRect();
          let shift = 0;
          if (fitted.left < panelRect.left + 3) shift = panelRect.left + 3 - fitted.left;
          else if (fitted.right > panelRect.right - 3) shift = panelRect.right - 3 - fitted.right;
          if (shift) chip.style.transform = 'translateX(' + shift.toFixed(1) + 'px)';
        });
      }

      // If the pre-selected card differs from the variant currently in the
      // product form (selected_or_first_available_variant), dispatch a change
      // so the theme syncs price, media, URL and the hidden [name="id"] input.
      initDefaultSelection() {
        const checked = this.querySelector('input[type="radio"][data-bvc-radio]:checked');
        if (!checked || !checked.dataset.variantId) return;
        const form = document.getElementById('product-form-' + this.sectionId);
        const idInput = form && form.querySelector('input[name="id"]');
        if (idInput && idInput.value !== checked.dataset.variantId) {
          checked.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onChange(event) {
        if (event.target && event.target.matches('input[type="radio"][data-bvc-radio]')) {
          this.syncState();
        }
      }

      syncState() {
        const checked = this.querySelector('input[type="radio"][data-bvc-radio]:checked');
        const value = checked ? checked.value : null;

        // Keep the checked ATTRIBUTE consistent with the checked property so
        // attribute-based selectors used by the theme stay reliable.
        this.querySelectorAll('input[type="radio"][data-bvc-radio]').forEach((input) => {
          if (input.checked) input.setAttribute('checked', '');
          else input.removeAttribute('checked');
        });

        // Activate only the gift attached to the selected offer AND matching
        // the current purchase mode (one-time / subscription). Elements with
        // class "product-info-upsell-{section}" and data-selected="true" are
        // automatically added to the cart by the theme's main Add to Cart.
        const mode = this.subMode ? 'sub' : 'ot';
        this.querySelectorAll('[data-bvc-gift-for]').forEach((gift) => {
          const giftMode = gift.dataset.bvcGiftMode || 'ot';
          const active = value !== null && gift.dataset.bvcGiftFor === value && giftMode === mode;
          gift.dataset.selected = active ? 'true' : 'false';
          gift.classList.toggle('bvc-gift--active', active);
        });

        // Progress bar: highlight the selected step and fill the track up to
        // it. Segments fill/unfill in sequence (cascading transition delays)
        // so the bar visibly advances or retreats as one continuous wave.
        const steps = Array.from(this.querySelectorAll('[data-bvc-step-for]'));
        const activeIndex = steps.findIndex((step) => value !== null && step.dataset.bvcStepFor === value);
        const prevIndex = typeof this.lastStepIndex === 'number' ? this.lastStepIndex : activeIndex;
        const SEGMENT_DELAY = 0.2; // seconds between consecutive segments
        steps.forEach((step, index) => {
          let delay = 0;
          if (activeIndex >= 0 && prevIndex >= 0) {
            if (activeIndex > prevIndex && index > prevIndex && index <= activeIndex) {
              delay = (index - prevIndex - 1) * SEGMENT_DELAY; // advancing left -> right
            } else if (activeIndex < prevIndex && index > activeIndex && index <= prevIndex) {
              delay = (prevIndex - index) * SEGMENT_DELAY; // retreating right -> left
            }
          }
          step.style.setProperty('--bpb-seg-delay', delay + 's');
          step.classList.toggle('bpb-step--active', index === activeIndex);
          step.classList.toggle('bpb-step--reached', activeIndex >= 0 && index <= activeIndex);
        });
        this.lastStepIndex = activeIndex;
      }
    }
  );
}

if (!customElements.get('bundle-subscription-widget')) {
  customElements.define(
    'bundle-subscription-widget',
    class BundleSubscriptionWidget extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.planId = this.dataset.planId || '';
        this.badgeMode = this.dataset.badgeMode || 'amount';
        const mapEl = this.querySelector('[data-bsw-map]');
        try {
          this.map = mapEl ? JSON.parse(mapEl.textContent) : {};
        } catch (e) {
          this.map = {};
        }

        this.onChangeBound = (event) => {
          if (event.target && event.target.matches && event.target.matches('input[data-bsw-radio]')) {
            this.apply();
          }
        };
        this.addEventListener('change', this.onChangeBound);

        // Instant sync when an offer card of this section is selected.
        this.onDocumentChange = (event) => {
          const input = event.target;
          if (!input || !input.matches || !input.matches('input[data-bvc-radio]')) return;
          const host = input.closest('bundle-variant-cards');
          if (!host || String(host.dataset.section) !== String(this.sectionId)) return;
          if (input.dataset.variantId) this.update(String(input.dataset.variantId));
        };
        document.addEventListener('change', this.onDocumentChange);

        // Follow variant changes from any other picker of this section.
        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe('variant-change', (event) => {
            if (!event || !event.data || String(event.data.sectionId) !== String(this.sectionId)) return;
            const variant = event.data.variant;
            if (!variant) return;
            // Ignore stale publishes: the section's form input is the source of truth.
            const formInput = document.querySelector('#product-form-' + this.sectionId + ' input[name="id"]');
            if (formInput && String(variant.id) !== String(formInput.value)) return;
            this.update(String(variant.id));
          });
        }

        // Initial variant: main product form, else the checked card, else first mapped.
        let initial = null;
        const mainInput = document.querySelector('#product-form-' + this.sectionId + ' input[name="id"]');
        if (mainInput && mainInput.value && this.map[mainInput.value]) initial = mainInput.value;
        if (!initial) {
          const cardRadio = document.querySelector(
            'bundle-variant-cards[data-section="' + this.sectionId + '"] input[data-bvc-radio]:checked'
          );
          if (cardRadio && cardRadio.dataset.variantId) initial = cardRadio.dataset.variantId;
        }
        if (!initial) initial = Object.keys(this.map)[0];
        if (initial) this.update(String(initial));
        this.apply();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChangeBound);
        document.removeEventListener('change', this.onDocumentChange);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      isSub() {
        const checked = this.querySelector('input[data-bsw-radio]:checked');
        return !!checked && checked.value === 'sub';
      }

      // Sync the hidden selling_plan input into this section's product forms
      // (bundle ATC + native form) and let the ATC button + cards react.
      apply() {
        const sub = this.isSub();
        if (this.planId) {
          const forms = document.querySelectorAll(
            'bundle-atc-button[data-section="' + this.sectionId + '"] form[data-type="add-to-cart-form"], #product-form-' + this.sectionId
          );
          forms.forEach((form) => {
            let input = form.querySelector('input[name="selling_plan"]');
            if (sub) {
              if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = 'selling_plan';
                form.appendChild(input);
              }
              input.value = this.planId;
            } else if (input) {
              input.remove();
            }
          });
        }
        // Dispatched even in editor demo mode (no planId) so mode-dependent
        // gifts still preview correctly; forms above stay untouched then.
        document.dispatchEvent(
          new CustomEvent('bundle:selling-plan-change', {
            detail: { sectionId: this.sectionId, sub: sub, planId: this.planId || null }
          })
        );
      }

      setText(selector, text) {
        const el = this.querySelector(selector);
        if (el) el.textContent = text == null ? '' : text;
      }

      update(variantId) {
        const entry = this.map[variantId];
        if (!entry) return;
        this.setText('[data-bsw-price-ot]', entry.ot.p);
        this.setText('[data-bsw-compare-ot]', entry.ot.c);
        this.setText('[data-bsw-unit-ot]', entry.ot.u);
        if (entry.sub) {
          this.setText('[data-bsw-price-sub]', entry.sub.p);
          this.setText('[data-bsw-compare-sub]', entry.sub.c);
          this.setText('[data-bsw-unit-sub]', entry.sub.u);
          if (this.badgeMode === 'amount') this.setText('[data-bsw-badge]', entry.badge_amount);
          else if (this.badgeMode === 'percent') this.setText('[data-bsw-badge]', entry.badge_percent);
        }
      }
    }
  );
}

if (!customElements.get('bundle-atc-button')) {
  customElements.define(
    'bundle-atc-button',
    class BundleAtcButton extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.idInput = this.querySelector('input[name="id"]');
        this.button = this.querySelector('button[type="submit"]');
        this.labelEl = this.querySelector('[data-bvc-atc-label]');
        this.priceEl = this.querySelector('[data-bvc-atc-price]');
        this.compareEl = this.querySelector('[data-bvc-atc-compare]');
        this.defaultLabel = this.labelEl ? this.labelEl.textContent : '';
        const mapEl = this.querySelector('[data-bvc-price-map]');
        try {
          this.priceMap = mapEl ? JSON.parse(mapEl.textContent) : {};
        } catch (e) {
          this.priceMap = {};
        }

        // Instant sync when an offer card of this section is selected —
        // no need to wait for the theme's section-render fetch.
        this.onDocumentChange = (event) => {
          const input = event.target;
          if (!input || !input.matches || !input.matches('input[data-bvc-radio]')) return;
          const host = input.closest('bundle-variant-cards');
          if (!host || String(host.dataset.section) !== String(this.sectionId)) return;
          if (input.dataset.variantId) this.update(String(input.dataset.variantId));
        };
        document.addEventListener('change', this.onDocumentChange);

        // Follow variant changes coming from any other picker of this section
        // (default picker, sticky ATC...).
        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe('variant-change', (event) => {
            if (!event || !event.data || String(event.data.sectionId) !== String(this.sectionId)) return;
            const variant = event.data.variant;
            if (!variant) return;
            // Ignore stale publishes (slow fetches resolving after a newer selection).
            const formInput = document.querySelector('#product-form-' + this.sectionId + ' input[name="id"]');
            if (formInput && String(variant.id) !== String(formInput.value)) return;
            this.update(String(variant.id));
          });
        }

        // Re-render the price when the subscription widget toggles the
        // selling_plan input inside our form.
        this.onPlanChange = (event) => {
          if (!event.detail || String(event.detail.sectionId) !== String(this.sectionId)) return;
          if (this.idInput && this.idInput.value) this.update(String(this.idInput.value));
        };
        document.addEventListener('bundle:selling-plan-change', this.onPlanChange);

        // Initial sync with the main product form if it already differs
        // (e.g. the cards block applied a pre-selected offer before us), or
        // if the subscription widget already put a selling_plan in our form.
        const mainInput = document.querySelector('#product-form-' + this.sectionId + ' input[name="id"]');
        if (mainInput && this.idInput && mainInput.value && mainInput.value !== this.idInput.value) {
          this.update(String(mainInput.value));
        } else if (this.idInput && this.idInput.value && this.querySelector('input[name="selling_plan"]')) {
          this.update(String(this.idInput.value));
        }

        // Keep the label + price pill on a single line: shrink the content
        // font-size until it fits the button width.
        this.contentEl = this.querySelector('.bvc-atc-btn__content');
        this.fitContent();
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.fitContent());
        }
        this.onResize = () => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => this.fitContent(), 150);
        };
        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onDocumentChange);
        document.removeEventListener('bundle:selling-plan-change', this.onPlanChange);
        window.removeEventListener('resize', this.onResize);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      fitContent() {
        if (!this.contentEl || !this.button) return;
        this.contentEl.style.fontSize = '';
        const buttonStyle = getComputedStyle(this.button);
        const available =
          this.button.clientWidth - parseFloat(buttonStyle.paddingLeft) - parseFloat(buttonStyle.paddingRight);
        if (available <= 0) return;
        let size = parseFloat(getComputedStyle(this.contentEl).fontSize);
        let guard = 24;
        while (this.contentEl.scrollWidth > available && size > 10 && guard-- > 0) {
          size -= 0.5;
          this.contentEl.style.fontSize = size + 'px';
        }
      }

      update(variantId) {
        if (this.idInput) this.idInput.value = variantId;
        const entry = this.priceMap[variantId];
        if (!entry) return;
        // When the subscription widget put a selling_plan input in our form,
        // show that plan's allocation price instead of the one-time price.
        const spInput = this.querySelector('input[name="selling_plan"]');
        const planEntry =
          spInput && spInput.value && entry.plans ? entry.plans[spInput.value] : null;
        const price = planEntry ? planEntry.price : entry.price;
        const compare = planEntry ? planEntry.compare : entry.compare;
        if (this.priceEl) this.priceEl.textContent = price;
        if (this.compareEl) {
          if (compare) {
            this.compareEl.textContent = compare;
            this.compareEl.classList.remove('hidden');
          } else {
            this.compareEl.classList.add('hidden');
          }
        }
        if (this.button) {
          if (entry.available) {
            this.button.removeAttribute('disabled');
            if (this.labelEl) this.labelEl.textContent = this.defaultLabel;
          } else {
            this.button.setAttribute('disabled', '');
            if (this.labelEl && this.dataset.soldoutText) this.labelEl.textContent = this.dataset.soldoutText;
          }
        }
        this.fitContent();
      }
    }
  );
}
